import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { Modal, ModalHeader, ModalTitle, ModalBody } from "react-bootstrap";
import ChangePassword from "../../content/UpdateUserForm.js";
import { oidcConfig } from "../../config/oidcConfig";
import { logoutFromKeycloakAndClear } from "../../services/authTokens";

import {
  Box,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Flex,
  Avatar,
  HStack,
  VStack,
  Divider,
  Badge,
  IconButton,
  useColorModeValue,
  Container,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  MenuDivider,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Icon,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {
  RiLogoutBoxLine,
  RiDashboardLine,
  RiUserSettingsLine,
  RiNotificationLine,
  RiSettings3Line,
  RiMenuLine,
  RiUser3Line,
  RiLockPasswordLine,
  RiShieldUserLine,
} from "react-icons/ri";
import {
  FiChevronDown,
  FiUser,
  FiSettings,
  FiBell,
  FiMenu,
  FiHome,
  FiUsers,
} from "react-icons/fi";
import { HiOutlineOfficeBuilding } from "react-icons/hi";

// Image
import logoKT from "../../assets/logoKT - transparent.png";
import logoKTSymbol from "../../assets/logoKT - Symbol.png";

function Header() {
  const [showModal, setShowModal] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const toast = useToast();

  // Color mode values
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const activeColor = useColorModeValue("blue.600", "blue.300");

  const user = localStorage.getItem("username");
  const employeeId = localStorage.getItem("id");
  const profilePicture = localStorage.getItem("profile_picture");
  const userRole = localStorage.getItem("role") || "Admin";

  const navItems = [
    {
      href: "/dashboard",
      text: "Dashboard",
      icon: RiDashboardLine,
      description: "Overview and analytics"
    },
    {
      href: "/manage-user",
      text: "Customer Management",
      icon: RiUserSettingsLine,
      description: "Manage customers and companies"
    },
    // { 
    //   href: "/landing-page", 
    //   text: "Landing Page Management",
    //   icon: HiOutlineOfficeBuilding,
    //   description: "Manage landing page content"
    // },
  ];

  // Get current page info
  const getCurrentPageInfo = () => {
    const currentItem = navItems.find(item => item.href === currentPath);
    return currentItem || { text: "Admin Panel", description: "Management Dashboard" };
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Confirm Logout",
      text: "Are you sure you want to log out?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#e53e3e",
      cancelButtonColor: "#718096",
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        content: 'swal-custom-content'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // Show success message
        toast({
          title: "Logged out successfully",
          description: "You have been logged out of your account",
          status: "success",
          duration: 2000,
          isClosable: true,
        });

        setTimeout(() => {
          logoutFromKeycloakAndClear();
        }, 500);
      }
    });
  };

  const handleChangePass = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleNavigate = (href) => {
    setCurrentPath(href);
    window.location.href = href;
  };

  // Update current path when location changes
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  const currentPageInfo = getCurrentPageInfo();

  return (
    <>
      <Box
        as="header"
        bg={bg}
        borderBottom="1px"
        borderColor={borderColor}
        position="sticky"
        top={0}
        zIndex={1000}
        shadow="sm"
        className={`header items-center justify-between w-full z-20 px-4 sm:px-6 md:px-8 lg:px-10 py-3 md:py-4 lg:py-5`}

      >
        <Flex align="center" justify="space-between" py={4}>
          {/* Left: Logo and Navigation */}
          <HStack spacing={8}>
            {/* Logo */}
            <HStack spacing={3} cursor="pointer" onClick={() => handleNavigate("/dashboard")}>
              <Box w="40px" h="40px">
                <img
                  src={logoKTSymbol}
                  alt="KejarTugas"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>
              <VStack align="start" spacing={0} display={{ base: "none", md: "flex" }}>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  KejarTugas
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                  Admin Panel
                </Text>
              </VStack>
            </HStack>
          </HStack>

          {/* Right: Actions and User Menu */}
          <HStack spacing={4}>
            {/* Desktop Navigation */}
            <HStack spacing={1} display={{ base: "none", lg: "flex" }}>
              {navItems.map((item) => {
                const isActive = currentPath === item.href;
                return (
                  <Tooltip key={item.href} label={item.description} hasArrow placement="bottom">
                    <Button
                      variant={isActive ? "solid" : "ghost"}
                      colorScheme={isActive ? "blue" : "gray"}
                      size="sm"
                      leftIcon={<Icon as={item.icon} />}
                      onClick={() => handleNavigate(item.href)}
                      fontWeight={isActive ? "semibold" : "medium"}
                      _hover={{
                        bg: isActive ? "blue.600" : "gray.100",
                        transform: "translateY(-1px)"
                      }}
                      transition="all 0.2s"
                    >
                      {item.text}
                    </Button>
                  </Tooltip>
                );
              })}
            </HStack>

            {/* Mobile Menu Button */}
            <IconButton
              display={{ base: "flex", lg: "none" }}
              variant="ghost"
              icon={<FiMenu />}
              onClick={onDrawerOpen}
              aria-label="Open menu"
            />

            {/* Notifications */}
            {/* <Tooltip label="Notifications" hasArrow>
              <IconButton
                variant="ghost"
                icon={<FiBell />}
                size="sm"
                position="relative"
                _hover={{ bg: "gray.100" }}
              >
                <Badge
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  position="absolute"
                  top="2px"
                  right="2px"
                  fontSize="10px"
                  minW="16px"
                  h="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  3
                </Badge>
              </IconButton>
            </Tooltip> */}

            {/* User Menu */}
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                size="sm"
                _hover={{ bg: "gray.100" }}
                _active={{ bg: "gray.200" }}
                rightIcon={<FiChevronDown size={12} />}
              >
                <HStack spacing={3}>
                  <Avatar
                    size="sm"
                    src={
                      profilePicture
                        ? `${process.env.REACT_APP_API_URL}/${profilePicture}`
                        : "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                    }
                    name={user}
                    bg="blue.500"
                  />
                  <VStack align="start" spacing={0} display={{ base: "none", md: "flex" }}>
                    <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                      {user || "Admin User"}
                    </Text>
                    <Badge size="sm" colorScheme="blue" variant="subtle">
                      {userRole}
                    </Badge>
                  </VStack>
                </HStack>
              </MenuButton>

              <MenuList shadow="lg" border="1px" borderColor={borderColor} py={2}>
                {/* User Info Header */}
                <Box px={4} py={3} bg={useColorModeValue("gray.50", "gray.700")}>
                  <HStack spacing={3}>
                    <Avatar
                      size="md"
                      src={
                        profilePicture
                          ? `${process.env.REACT_APP_API_URL}/${profilePicture}`
                          : "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                      }
                      name={user}
                      bg="blue.500"
                    />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {user || "Admin User"}
                      </Text>
                      <Badge size="sm" colorScheme="blue" variant="subtle">
                        {userRole}
                      </Badge>
                    </VStack>
                  </HStack>
                </Box>

                <MenuDivider />

                {/* Menu Items */}
                {/* <MenuItem icon={<RiUser3Line />} fontSize="sm">
                  <Text>View Profile</Text>
                </MenuItem>

                <MenuItem icon={<RiLockPasswordLine />} onClick={handleChangePass} fontSize="sm">
                  <Text>Change Password</Text>
                </MenuItem>

                <MenuItem icon={<RiSettings3Line />} fontSize="sm">
                  <Text>Account Settings</Text>
                </MenuItem>

                <MenuDivider /> */}

                <MenuItem
                  icon={<RiLogoutBoxLine />}
                  onClick={handleLogout}
                  fontSize="sm"
                  color="red.500"
                  _hover={{ bg: "red.50", color: "red.600" }}
                >
                  <Text>Log Out</Text>
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </Flex>
      </Box>

      {/* Mobile Navigation Drawer */}
      <Drawer isOpen={isDrawerOpen} placement="left" onClose={onDrawerClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <HStack spacing={3}>
              <Box w="32px" h="32px">
                <img
                  src={logoKTSymbol}
                  alt="KejarTugas"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="bold">
                  KejarTugas
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Admin Panel
                </Text>
              </VStack>
            </HStack>
          </DrawerHeader>

          <DrawerBody px={0}>
            <VStack spacing={1} align="stretch">
              {navItems.map((item) => {
                const isActive = currentPath === item.href;
                return (
                  <Button
                    key={item.href}
                    variant={isActive ? "solid" : "ghost"}
                    colorScheme={isActive ? "blue" : "gray"}
                    justifyContent="flex-start"
                    leftIcon={<Icon as={item.icon} />}
                    onClick={() => {
                      handleNavigate(item.href);
                      onDrawerClose();
                    }}
                    fontWeight={isActive ? "semibold" : "medium"}
                    size="lg"
                    borderRadius="none"
                    px={6}
                  >
                    <VStack align="start" spacing={0} flex={1}>
                      <Text>{item.text}</Text>
                      <Text fontSize="xs" color="gray.500" fontWeight="normal">
                        {item.description}
                      </Text>
                    </VStack>
                  </Button>
                );
              })}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Change Password Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="md" centered>
        <ModalHeader>
          <ModalTitle>
            <HStack spacing={2}>
              <RiLockPasswordLine />
              <Text>{user}'s Profile Settings</Text>
            </HStack>
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <ChangePassword onClose={handleCloseModal} />
        </ModalBody>
      </Modal>
    </>
  );
}

export default Header;